import { Flex, Text, Input, DropDownMenu, DropdownContainer, DropDownButton } from '@upsales/components';
import { useAIChatSelector, type ChatSession } from '../../Context/AIChatContext';
import { useAIChatDispatch } from '../../Context/AIChatContext';
import { renameChat, deleteChat } from '../../Context/Helpers';
import { useTranslation } from 'Components/Helpers/translate';
import React, { useState, useRef, useEffect } from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import DropdownItem from 'App/components/DropdownItem';
import { getChatDateString } from '../helpers';
import UnreadBadge from '../UnreadBadge';

import './Conversation.scss';

const Conversation = ({ chat, onClick }: { chat: ChatSession; onClick: () => void }) => {
	const { t } = useTranslation();
	const dispatch = useAIChatDispatch();
	const { currentChatId } = useAIChatSelector(({ currentChatId }) => ({ currentChatId }));
	const [isEditing, setIsEditing] = useState(false);
	const [editValue, setEditValue] = useState(chat.name);
	const [isHoveringBadge, setIsHoveringBadge] = useState(false);
	const [isHoveringItem, setIsHoveringItem] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);
	const hasUnread = (chat.unreadCount ?? 0) > 0;

	const chatName = chat.name && chat.name.trim() ? chat.name : t('aiChat.untitledChat', 'Untitled chat');
	const dateStr = getChatDateString(new Date(chat.updatedAt || chat.createdAt));

	const isActiveChat = chat.id === currentChatId;

	const classes = new BemClass('Conversation');

	useEffect(() => {
		setEditValue(chat.name);
	}, [chat.name]);

	useEffect(() => {
		if (isEditing) {
			const input = inputRef.current;
			if (input) {
				input.focus();
				input.select();
			}
		}
	}, [isEditing]);

	const handleSave = async () => {
		const trimmedValue = editValue.trim();
		if (trimmedValue && trimmedValue !== chat.name) {
			dispatch(renameChat(chat.id, trimmedValue));
		} else {
			setEditValue(chat.name);
		}
		setIsEditing(false);
	};

	const handleDelete = async () => {
		dispatch(deleteChat(chat.id));
	};

	const handleCancel = () => {
		setEditValue(chat.name);
		setIsEditing(false);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleSave();
		} else if (e.key === 'Escape') {
			e.preventDefault();
			handleCancel();
		}
	};

	const renderDropdownMenu = () => (
		<DropDownMenu
			align="right"
			noMinWidth
			useAnimation
			arrowed={false}
			renderTrigger={(isExpanded, setExpanded) => (
				<DropDownButton
					icon="ellipsis-v"
					expanded={isExpanded}
					onClick={(e: Parameters<typeof setExpanded>[0]) => {
						e.stopPropagation();
						setExpanded(e);
					}}
					hideArrow
					size="sm"
					className={classes.elem('menuButton').b()}
				/>
			)}
		>
			{closeMenu => (
				<DropdownContainer space="ptm prm pbm plm">
					<Flex direction="column">
						<DropdownItem
							icon="edit"
							title={t('aiChat.renameChat', 'Rename chat')}
							onClick={e => {
								e.stopPropagation();
								setIsEditing(true);
								closeMenu();
							}}
						/>

						<DropdownItem
							icon="trash"
							title={t('default.delete', 'Delete')}
							onClick={e => {
								e.stopPropagation();
								handleDelete();
								closeMenu();
							}}
						/>
					</Flex>
				</DropdownContainer>
			)}
		</DropDownMenu>
	);

	return (
		<Flex
			className={classes.mod({ active: isActiveChat }).b()}
			spacing="pl3 pr3 pt1 pb1 ml1 mr1"
			alignItems="center"
			justifyContent="space-between"
			onMouseEnter={() => setIsHoveringItem(true)}
			onMouseLeave={() => setIsHoveringItem(false)}
			onClick={e => {
				if (!isEditing) {
					e.stopPropagation();
					onClick();
				}
			}}
		>
			<Flex gap="u1" direction="column" className={classes.elem('content').mod({ hovering: isHoveringItem }).b()}>
				<Flex gap="u1" alignItems="center" justifyContent="space-between">
					{isEditing ? (
						<Input
							inputRef={inputRef}
							size="sm"
							value={editValue}
							onChange={e => setEditValue(e.target.value)}
							onBlur={handleSave}
							onKeyDown={handleKeyDown}
							onClick={e => e.stopPropagation()}
							className={classes.elem('editInput').b()}
						/>
					) : (
						<Text ellipsis bold={isActiveChat}>
							{chatName}
						</Text>
					)}
				</Flex>
				<Text size="xs" color="grey-7">
					{dateStr}
				</Text>
			</Flex>

			<div
				className={classes.elem('badgeContainer').b()}
				onMouseEnter={() => setIsHoveringBadge(true)}
				onMouseLeave={() => setIsHoveringBadge(false)}
			>
				{hasUnread ? (
					isHoveringBadge ? (
						renderDropdownMenu()
					) : (
						<UnreadBadge count={chat.unreadCount ?? 0} size="lg" />
					)
				) : isHoveringItem ? (
					renderDropdownMenu()
				) : null}
			</div>
		</Flex>
	);
};

export default Conversation;
